import { Typography, Form, FormInstance, Tabs, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { CategoryStepUIConfig } from '../../../shared/CategoryStepUIConfig';
import { QuestionsToAsk } from '../QuestionsToAsk';
import { FeedbackComponent } from '../Feedback';
import { MandatoryQuestionsToAsk } from '../MandatoryQuestionsToAsk';
import {
	Answers,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { MultipleChoiceQuestionUIConfig } from '../../../shared/MultipleChoiceQuestionUIConfig';
import { ApplicantAnswerToQuestion } from '../ApplicantAnswerToQuestion';
import { useLocation } from 'react-router-dom';
import { VIEW_FAMILY_MEMBER_INTERVIEW } from '../../../shared/Routes';
import useUserSession from '../../../hooks/useUserSession';
import { SELECTED_ROLES } from '../../../shared/Utils';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface CategoryStepProps {
	uiConfig: CategoryStepUIConfig;
	form: FormInstance;
	mandatoryQuestionsAndAnswersForm: FormInstance;
	areCommentsMandatory?: boolean;
	answers?: Answers;
	showApplicantAnswers?: boolean;
	checkForCareTeamQuestions?: boolean;
}

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description: string;
}) => {
	return (
		<div className="hidden sm:flex flex-col mb-[8px]">
			<Text className="font-sans font-bold text-lg text-primary-blue">
				{heading}
			</Text>
			<Text className="font-sans font-normal text-sm mb-[8px]" type="secondary">
				{description}
			</Text>
		</div>
	);
};

export const CategoryStep = ({
	uiConfig: {
		title,
		functionaryTitle,
		subTitle,
		questionsToAskInFamilyMemberInterview,
		questions,
	},
	form,
	mandatoryQuestionsAndAnswersForm,
	areCommentsMandatory,
	answers,
	showApplicantAnswers = true,
	checkForCareTeamQuestions = false,
}: CategoryStepProps) => {
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { selectedRole } = useUserSession();
	const { pathname } = useLocation();

	const isRoleAllowed = SELECTED_ROLES.includes(selectedRole as UserRole);

	const isProposerAndViewable =
		(selectedRole === UserRole.PROPOSER &&
			pathname.includes(VIEW_FAMILY_MEMBER_INTERVIEW)) ||
		checkForCareTeamQuestions;

	const disableEdit = isRoleAllowed || isProposerAndViewable;

	const filteredQuestions = checkForCareTeamQuestions
		? questions.filter(
				(question: MultipleChoiceQuestionUIConfig) =>
					question.isCareTeamQuestion
		  )
		: questions;

	const allQuestions = filteredQuestions.map(
		(question: MultipleChoiceQuestionUIConfig) => {
			const value = answers?.questionsAndAnswers.find(
				(answer) => answer.questionId === question.questionId
			);
			return (
				<div key={question.questionId} className="my-[8px]">
					<ApplicantAnswerToQuestion
						uiConfig={question}
						value={value}
						showBG={isMobile ? true : false}
					/>
				</div>
			);
		}
	);

	const filteredMandatoryQuestions = checkForCareTeamQuestions
		? questionsToAskInFamilyMemberInterview.mandatoryQuestionsToAsk.filter(
				(question: MultipleChoiceQuestionUIConfig) =>
					question.isCareTeamQuestion
		  )
		: questionsToAskInFamilyMemberInterview.mandatoryQuestionsToAsk;

	return (
		<div className="flex flex-col h-full min-h-0 max-w-[600px]">
			<Form
				name="mandatoryQuestionsAndAnswersForm"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				style={{ maxWidth: 600 }}
				initialValues={{ remember: true }}
				autoComplete="off"
				form={mandatoryQuestionsAndAnswersForm}
			>
				<SectionHeader
					heading={functionaryTitle ? t(functionaryTitle) : t(title)}
					description={t(subTitle)}
				/>
				<Tabs
					defaultActiveKey="1"
					centered
					tabBarStyle={{ fontWeight: 'bold' }}
				>
					<Tabs.TabPane
						className="w-full"
						tab={<div>{t('mandatoryQuestionsForFamily')}</div>}
						key="1"
					>
						<MandatoryQuestionsToAsk
							uiConfig={filteredMandatoryQuestions.map((question) => ({
								...question,
								disabled: disableEdit,
							}))}
						/>
					</Tabs.TabPane>
					{showApplicantAnswers && (
						<Tabs.TabPane
							className="w-full"
							tab={t('candidateApplication')}
							key="2"
						>
							{allQuestions}
						</Tabs.TabPane>
					)}
				</Tabs>
			</Form>
			<Form
				name="basic"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				style={{ maxWidth: 600 }}
				initialValues={{ remember: true }}
				autoComplete="off"
				form={form}
			>
				<QuestionsToAsk
					uiConfig={
						questionsToAskInFamilyMemberInterview.suggestedQuestionsToAsk
					}
				/>
				<Divider className="border border-solid border-gray-200" />
				<p className="font-sans font-bold text-lg text-primary-blue">
					{t('proposersFeedbackForCategory', { categoryTitle: t(title) })}
				</p>
				<FeedbackComponent
					form={form}
					commentsMandatory={areCommentsMandatory}
					disabled={disableEdit}
				/>
			</Form>
		</div>
	);
};
